/**
 * Appointment-booking queries over the local nomenclature cache.
 *
 * This is the web port of the mobile app's `LocalAppointmentFilterService` +
 * `LocalPhysicianEntitiesService`. The booking flow is driven entirely by the
 * `azure_filter_combinations` table: every valid (city, location, specialty,
 * physician, entity, investigation) tuple has exactly one row, so the cascading
 * dropdowns are just DISTINCT projections of that table filtered by the current
 * partial selection — identical to the SQL the app runs.
 *
 * All `*_id` columns in `azure_filter_combinations` are LOCAL integer foreign
 * keys (→ each table's `id`). The MEDIS UUIDs the API needs live on the
 * reference rows: `physicians.master_physician_id`, `specialties.specialty_id`,
 * `locations.location_id`, `investigations.inv_id`, and the per-entity timetable
 * UUID on `physician_entities.entity_id`.
 */

import { db, type NomenclatureRow } from './db';
import { revealRows } from './crypto';

// ─── Option shapes returned to the UI ───────────────────────────────────────

export interface CityOption {
  id: number;
  name: string;
}
export interface LocationOption {
  id: number;
  name: string;
  /** MEDIS UUID (LocationID) for timetable/booking calls. */
  locationId: string;
  address?: string;
  town?: string;
  county?: string;
}
export interface SpecialtyOption {
  id: number;
  name: string;
  /** MEDIS UUID (SpecialityID). */
  specialityId: string;
}
export interface ServiceOption {
  id: number;
  name: string;
  /** MEDIS UUID (InvestigationID). */
  invId: string;
}
export interface DoctorOption {
  id: number;
  name: string;
  /** MEDIS UUID (master_physician_id → PhysicianID). */
  physicianId: string;
}

export interface FilterData {
  cities: CityOption[];
  locations: LocationOption[];
  specialties: SpecialtyOption[];
  services: ServiceOption[];
  doctors: DoctorOption[];
}

export interface FilterSelection {
  cityId?: number | null;
  locationId?: number | null;
  specialtyId?: number | null;
  investigationId?: number | null;
  physicianId?: number | null;
}

/** A physician entity valid for the current filters (drives timetable lookups). */
export interface EntityDetail {
  /** MEDIS entity UUID — matches the `EntityID` on timetable days. */
  entityId: string;
  physicianId: number;
  physicianName: string;
  specialtyId: number | null;
  locationId: number | null;
}

// ─── Cached projections (rebuilt after every sync) ──────────────────────────

interface RefData {
  citiesById: Map<number, { id: number; name: string }>;
  locationsById: Map<
    number,
    { id: number; name: string; locationId: string; address?: string; town?: string; county?: string; active: boolean }
  >;
  specialtiesById: Map<number, { id: number; name: string; specialityId: string }>;
  investigationsById: Map<number, { id: number; name: string; invId: string }>;
  physiciansById: Map<number, { id: number; fullName: string; masterPhysicianId: string; active: boolean }>;
}

let refDataCache: Promise<RefData> | null = null;
let combosCache: Promise<NomenclatureRow[]> | null = null;
let entitiesCache: Promise<NomenclatureRow[]> | null = null;

/** Drop the in-memory caches — call after a nomenclature sync or on logout. */
export function invalidateAppointmentCache(): void {
  refDataCache = null;
  combosCache = null;
  entitiesCache = null;
}

const isActive = (v: unknown): boolean => v !== 0 && v !== false && v !== '0';

async function buildRefData(): Promise<RefData> {
  const [cities, locationsRaw, specialties, investigations, physiciansRaw] = await Promise.all([
    db.rows('cities').toArray(),
    db.rows('azure_locations').toArray(),
    db.rows('azure_specialties').toArray(),
    db.rows('azure_investigations').toArray(),
    db.rows('azure_physicians').toArray(),
  ]);

  // physicians (full_name) and locations (address) carry encrypted fields.
  const [locations, physicians] = await Promise.all([revealRows(locationsRaw), revealRows(physiciansRaw)]);

  const citiesById = new Map<number, { id: number; name: string }>();
  for (const c of cities) citiesById.set(c.id as number, { id: c.id as number, name: String(c.name ?? '') });

  const locationsById: RefData['locationsById'] = new Map();
  for (const l of locations) {
    const id = l.id as number;
    locationsById.set(id, {
      id,
      name: String(l.name ?? ''),
      locationId: String(l.location_id ?? ''),
      address: l.address != null ? String(l.address) : undefined,
      town: l.town != null ? String(l.town) : undefined,
      county: l.county != null ? String(l.county) : undefined,
      active: isActive(l.is_active),
    });
  }

  const specialtiesById: RefData['specialtiesById'] = new Map();
  for (const s of specialties) {
    const id = s.id as number;
    specialtiesById.set(id, { id, name: String(s.specialty_name ?? ''), specialityId: String(s.specialty_id ?? '') });
  }

  const investigationsById: RefData['investigationsById'] = new Map();
  for (const i of investigations) {
    const id = i.id as number;
    investigationsById.set(id, { id, name: String(i.name ?? ''), invId: String(i.inv_id ?? '') });
  }

  const physiciansById: RefData['physiciansById'] = new Map();
  for (const p of physicians) {
    const id = p.id as number;
    physiciansById.set(id, {
      id,
      fullName: String(p.full_name ?? ''),
      masterPhysicianId: String(p.master_physician_id ?? ''),
      active: isActive(p.is_active),
    });
  }

  return { citiesById, locationsById, specialtiesById, investigationsById, physiciansById };
}

function getRefData(): Promise<RefData> {
  if (!refDataCache) refDataCache = buildRefData();
  return refDataCache;
}

function getCombos(): Promise<NomenclatureRow[]> {
  // Only `price` is encrypted on combos; every id we filter on stays in clear.
  if (!combosCache) combosCache = db.rows('azure_filter_combinations').toArray();
  return combosCache;
}

function getEntities(): Promise<NomenclatureRow[]> {
  if (!entitiesCache) entitiesCache = db.rows('azure_physician_entities').toArray();
  return entitiesCache;
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'ro');

/**
 * Cascading filter options for the current selection — but FACETED, so the user
 * can fill the dropdowns in any order. Each dimension's option list is computed
 * by matching every OTHER selected constraint EXCEPT its own; that way a
 * dropdown always offers every value still reachable given the rest, and
 * picking it never locks the others. Combinations whose physician or location
 * is inactive are dropped first (the app's `p.is_active = 1 AND l.is_active =
 * 1`).
 */
export async function getFilterData(sel: FilterSelection): Promise<FilterData> {
  const [combos, ref] = await Promise.all([getCombos(), getRefData()]);

  const cityIds = new Set<number>();
  const locationIds = new Set<number>();
  const specialtyIds = new Set<number>();
  const investigationIds = new Set<number>();
  const physicianIds = new Set<number>();

  for (const c of combos) {
    const physicianId = c.physician_id as number;
    const locationId = c.location_id as number;

    // Active-only, matching the app's INNER JOIN + WHERE.
    if (!ref.physiciansById.get(physicianId)?.active) continue;
    if (!ref.locationsById.get(locationId)?.active) continue;

    // Per-constraint matches against this combo.
    const okCity = sel.cityId == null || c.city_id === sel.cityId;
    const okLocation = sel.locationId == null || locationId === sel.locationId;
    const okSpecialty = sel.specialtyId == null || c.specialty_id === sel.specialtyId;
    const okService = sel.investigationId == null || c.investigation_id === sel.investigationId;
    const okDoctor = sel.physicianId == null || physicianId === sel.physicianId;

    // A dimension's value is offered when every OTHER constraint matches.
    if (okLocation && okSpecialty && okService && okDoctor) cityIds.add(c.city_id as number);
    if (okCity && okSpecialty && okService && okDoctor) locationIds.add(locationId);
    if (okCity && okLocation && okService && okDoctor) specialtyIds.add(c.specialty_id as number);
    if (okCity && okLocation && okSpecialty && okDoctor) investigationIds.add(c.investigation_id as number);
    if (okCity && okLocation && okSpecialty && okService) physicianIds.add(physicianId);
  }

  const cities: CityOption[] = [...cityIds]
    .map((id) => ref.citiesById.get(id))
    .filter((c): c is NonNullable<typeof c> => !!c)
    .map((c) => ({ id: c.id, name: c.name }))
    .sort(byName);

  const locations: LocationOption[] = [...locationIds]
    .map((id) => ref.locationsById.get(id))
    .filter((l): l is NonNullable<typeof l> => !!l)
    .map((l) => ({ id: l.id, name: l.name, locationId: l.locationId, address: l.address, town: l.town, county: l.county }))
    .sort(byName);

  const specialties: SpecialtyOption[] = [...specialtyIds]
    .map((id) => ref.specialtiesById.get(id))
    .filter((s): s is NonNullable<typeof s> => !!s)
    .map((s) => ({ id: s.id, name: s.name, specialityId: s.specialityId }))
    .sort(byName);

  const services: ServiceOption[] = [...investigationIds]
    .map((id) => ref.investigationsById.get(id))
    .filter((i): i is NonNullable<typeof i> => !!i)
    .map((i) => ({ id: i.id, name: i.name, invId: i.invId }))
    .sort(byName);

  const doctors: DoctorOption[] = [...physicianIds]
    .map((id) => ref.physiciansById.get(id))
    .filter((p): p is NonNullable<typeof p> => !!p)
    .map((p) => ({ id: p.id, name: p.fullName, physicianId: p.masterPhysicianId }))
    .sort(byName);

  return { cities, locations, specialties, services, doctors };
}

/**
 * Physician entities valid for the current filters — the timetable's `EntityID`
 * universe. Port of `getAppointmentEntitiesWithDetails`: filter the entities by
 * (physician, specialty, location); when a service is selected, keep only
 * entities that have a price row for it (`prices.entity_id = entities.id AND
 * prices.inv_id = <service local id>`).
 */
export async function getValidEntities(sel: {
  locationId?: number | null;
  specialtyId?: number | null;
  physicianId?: number | null;
  investigationId?: number | null;
}): Promise<EntityDetail[]> {
  const [entities, ref] = await Promise.all([getEntities(), getRefData()]);

  let filtered = entities.filter((e) => {
    const entityId = e.entity_id;
    if (entityId == null || entityId === '') return false;
    if (sel.physicianId != null && e.physician_id !== sel.physicianId) return false;
    if (sel.specialtyId != null && e.specialty_id !== sel.specialtyId) return false;
    if (sel.locationId != null && e.location_id !== sel.locationId) return false;
    return true;
  });

  if (sel.investigationId != null) {
    const prices = await db.rows('azure_physician_prices').where('inv_id').equals(sel.investigationId).toArray();
    const validEntityPks = new Set(prices.map((p) => p.entity_id as number));
    filtered = filtered.filter((e) => validEntityPks.has(e.id));
  }

  const seen = new Set<string>();
  const out: EntityDetail[] = [];
  for (const e of filtered) {
    const entityId = String(e.entity_id);
    if (seen.has(entityId)) continue;
    seen.add(entityId);
    const physicianId = e.physician_id as number;
    out.push({
      entityId,
      physicianId,
      physicianName: ref.physiciansById.get(physicianId)?.fullName ?? '',
      specialtyId: (e.specialty_id as number) ?? null,
      locationId: (e.location_id as number) ?? null,
    });
  }
  return out;
}

/**
 * Price (RON) for a fully-specified combination, decrypted. All ids are LOCAL.
 * Mirrors the app's `LocalPricingService` lookup on `azure_physician_prices`.
 */
export async function getPrice(args: {
  physicianId: number;
  specialtyId: number;
  locationId: number;
  invId?: number | null;
}): Promise<number | null> {
  const candidates = await db.rows('azure_physician_prices').where('physician_id').equals(args.physicianId).toArray();
  const match = candidates.find(
    (c) =>
      c.specialty_id === args.specialtyId &&
      c.location_id === args.locationId &&
      (args.invId == null || c.inv_id === args.invId),
  );
  if (!match) return null;
  const [revealed] = await revealRows([match]);
  const price = revealed?.price;
  if (price == null) return null;
  return typeof price === 'number' ? price : Number(price);
}
